import { CURRENCY } from "@/data/numbers";
import { routes } from "@/data/routes";
import { type Cart } from "@/lib/cart";
import { cn } from "@/lib/components";
import { formatPrice } from "@/lib/numbers";
import { type Product } from "@repo/schemas";
import { Trash2Icon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "../base/button";
import { Input } from "../base/input";

export type CartItemsProps = {
  items: Cart["items"];
  onDelete: (productId: string) => void;
};

export function CartItems(props: CartItemsProps) {
  return (
    <ul className="flex flex-col gap-4">
      {props.items.map(({ product, quantity }) => {
        return (
          <li key={product.id} className="border-b border-gray-300 pb-4">
            <Link
              href={routes.productDetails.path.replace(":id", product.id)}
              className="grid grid-cols-[72px_1fr] gap-4"
            >
              <div className="relative aspect-square w-full">
                <Image
                  src={
                    (product.images.find((img) => img.type === "small") ?? product.images[0]).src
                  }
                  alt={product.title}
                  fill
                  sizes="72px"
                />
              </div>
              <div>
                <div className="flex">
                  <p className="flex-1 text-sm font-bold">{product.title}</p>
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      props.onDelete(product.id);
                    }}
                    variant="ghost"
                    size="wrapper"
                  >
                    <Trash2Icon className="size-4 self-start text-red-300" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500">Qty: {quantity}</p>
                <p className="text-right text-sm font-bold">
                  {formatPrice(product.price, CURRENCY)}
                </p>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

CartItems.Large = function CartItemsLarge(
  props: CartItemsProps & {
    onIncrement: (product: Product) => void;
    onDecrement: (product: Product) => void;
    className?: string;
  },
) {
  return (
    <ul className={cn("flex flex-col gap-4", props.className)}>
      {props.items.map(({ product, quantity }) => {
        return (
          <li
            key={product.id}
            className={cn(
              "border-b border-gray-300 pb-4",
              "grid grid-cols-[128px_1fr_128px] gap-4",
            )}
          >
            <Link
              href={routes.productDetails.path.replace(":id", product.id)}
              className="row-span-2"
            >
              <div className="relative aspect-square w-full">
                <Image
                  src={
                    (product.images.find((img) => img.type === "small") ?? product.images[0]).src
                  }
                  alt={product.title}
                  fill
                  sizes="128px"
                />
              </div>
            </Link>
            <p className="font-bold">{product.title}</p>
            <Button
              className="justify-self-end"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onDelete(product.id);
              }}
              variant="ghost"
              size="wrapper"
            >
              <Trash2Icon className="size-4 self-start text-red-300" />
            </Button>
            <p className="self-end text-sm text-gray-500">
              Qty:
              <Input
                type="number"
                value={quantity}
                onChange={(e) => {
                  const newQuantity = Number(e.target.value);
                  if (newQuantity > quantity) {
                    props.onIncrement(product);
                  } else if (newQuantity < quantity) {
                    props.onDecrement(product);
                  }
                }}
                className="max-w-fit"
                min={1}
                max={100}
              />
            </p>
            <p className="self-end text-right text-sm font-bold">
              {formatPrice(product.price, CURRENCY)}
            </p>
          </li>
        );
      })}
    </ul>
  );
};
